import { mountEventCardStack } from './EventCardStack';

const createElement = (tag, className, text = '') => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
};

const createArrowIcon = () => {
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('class', 'rg-inline-arrow');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('viewBox', '0 0 16 16');
    icon.setAttribute('focusable', 'false');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M4 12 12 4M6 4h6v6');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-linecap', 'square');
    path.setAttribute('stroke-width', '1.7');
    icon.append(path);

    return icon;
};

const renderFeaturedSlide = (event, index) => {
    const slide = createElement('div', 'rg-events-slide');
    const article = createElement('article', 'rg-event-ticket rg-event-ticket--featured');
    article.dataset.eventId = String(event.id);

    const media = createElement('figure', 'rg-event-ticket__media');
    const mediaLink = createElement('a', 'rg-event-ticket__media-link');
    mediaLink.href = event.url;
    mediaLink.dataset.cursorLabel = 'VOIR';
    const image = createElement('img');
    image.src = event.image;
    image.alt = event.title;
    image.loading = index === 0 ? 'eager' : 'lazy';
    mediaLink.append(image);
    media.append(mediaLink);
    const mediaTitle = createElement('div', 'rg-event-ticket__media-title');
    mediaTitle.append(createElement('h3', '', event.title));
    media.append(mediaTitle);

    const body = createElement('div', 'rg-event-ticket__body');
    const meta = createElement('div', 'rg-event-ticket__meta');
    meta.append(createElement('p', '', `${event.dateLabel} · ${event.location}`));
    body.append(meta);
    let price;
    if (event.isFree) {
        price = createElement('p', 'rg-event-ticket__price rg-event-ticket__price--panel rg-event-ticket__price--free', 'Gratuit');
    } else {
        price = createElement('p', 'rg-event-ticket__price rg-event-ticket__price--panel');
        price.append(createElement('strong', '', event.priceAmount || event.priceLabel || ''));
        price.append(createElement('span', '', 'FCFA'));
    }
    body.append(price);
    const footer = createElement('div', 'rg-event-ticket__footer');
    const transaction = createElement('div', 'rg-event-ticket__transaction');
    const countdown = createElement('div', 'rg-event-countdown');
    countdown.dataset.countdownStart = event.startDate || '';
    countdown.setAttribute('aria-label', `Compte à rebours avant ${event.title}`);
    [['days', 'Jours'], ['hours', 'Heures'], ['minutes', 'Minutes'], ['seconds', 'Secondes']].forEach(([key, label]) => {
        const unit = createElement('span');
        unit.append(createElement('strong', '', '--'));
        unit.lastChild.dataset[`countdown${key[0].toUpperCase()}${key.slice(1)}`] = '';
        unit.append(createElement('small', '', label));
        countdown.append(unit);
    });
    const link = createElement('a', 'rg-button rg-button--primary', "Voir l'événement ");
    link.href = event.url;
    link.dataset.cursorLabel = 'VOIR';
    link.append(createArrowIcon());
    transaction.append(link);
    footer.append(transaction, countdown);
    if (event.description) footer.append(createElement('p', 'rg-event-ticket__description', event.description));
    body.append(footer);

    article.append(media, body);
    slide.append(article);
    return slide;
};

const renderEditorialEntry = (event, index) => {
    const article = createElement('article', `rg-journal-entry rg-journal-entry--${index + 1} rg-reveal is-visible`);
    article.dataset.eventId = String(event.id);

    const link = createElement('a', 'rg-journal-image');
    link.href = event.url;
    link.dataset.cursorLabel = 'VOIR';
    const image = createElement('img');
    image.src = event.image;
    image.alt = event.title;
    image.loading = 'lazy';
    link.append(image);
    article.append(link);

    const details = createElement('div');
    details.append(createElement('p', '', `${event.dateLabel} · ${event.location}`));
    const headline = createElement('div', 'rg-journal-entry__headline');
    const title = createElement('h3');
    const titleLink = createElement('a', 'rg-link', event.title);
    titleLink.href = event.url;
    title.append(titleLink);
    headline.append(title);
    headline.append(createElement('span', 'rg-journal-entry__price', event.priceLabel || 'Gratuit'));
    details.append(headline);
    article.append(details);
    return article;
};

const renderSecondarySlide = (events) => {
    const slide = createElement('div', 'rg-events-slide');
    const grid = createElement('div', 'rg-journal-grid rg-events-editorial-pair');
    events.forEach((event, index) => grid.append(renderEditorialEntry(event, index)));
    slide.append(grid);
    return slide;
};

const updateCountdowns = (root) => {
    const activeCards = root.querySelectorAll('[data-events-featured-container] .rg-events-cardstack__card.is-active [data-countdown-start]');
    const countdowns = activeCards.length ? activeCards : root.querySelectorAll('[data-countdown-start]');
    countdowns.forEach((countdown) => {
        const start = Date.parse(countdown.dataset.countdownStart || '');
        if (!Number.isFinite(start)) return;

        const remaining = Math.max(0, start - Date.now());
        const values = {
            days: Math.floor(remaining / 86400000),
            hours: Math.floor((remaining % 86400000) / 3600000),
            minutes: Math.floor((remaining % 3600000) / 60000),
            seconds: Math.floor((remaining % 60000) / 1000),
        };

        Object.entries(values).forEach(([key, value]) => {
            const target = countdown.querySelector(`[data-countdown-${key}]`);
            if (target) target.textContent = remaining > 0 ? String(value).padStart(2, '0') : '--';
        });

        countdown.classList.toggle('is-started', remaining === 0);
        countdown.setAttribute('aria-label', remaining > 0 ? 'Compte à rebours actif' : 'Événement commencé ou terminé');
    });
};

export const mountEventsFeature = (root, { reducedMotion = false } = {}) => {
    const dataElement = root.querySelector('[data-modern-events-data]');
    const secondaryDataElement = root.querySelector('[data-modern-events-secondary-data]');
    const featuredViewport = root.querySelector('[data-events-featured-viewport]');
    const featuredContainer = root.querySelector('[data-events-featured-container]');
    const secondary = root.querySelector('.rg-events-secondary');
    const secondaryContainer = root.querySelector('[data-events-secondary-container]');
    if (!dataElement || !featuredViewport || !featuredContainer) return () => {};

    let events;
    try {
        events = JSON.parse(dataElement.textContent || '[]').filter((event) => event?.id && event?.title);
    } catch {
        return () => {};
    }
    if (!events.length) return () => {};

    let secondaryEvents = [];
    try {
        secondaryEvents = JSON.parse(secondaryDataElement?.textContent || '[]').filter((event) => event?.id && event?.title);
    } catch {
        secondaryEvents = [];
    }
    const eventCandidates = [...events, ...secondaryEvents].filter((event, index, collection) => (
        collection.findIndex((candidate) => candidate.id === event.id) === index
    ));

    let featuredStack;
    let countdownTimer;
    let active = true;
    const renderSecondary = (featuredId) => {
        if (!secondary || !secondaryContainer) return;
        const remaining = eventCandidates.filter((event) => event.id !== featuredId);
        const pairOptions = remaining.length >= 2
            ? [[remaining[0], remaining[1]]]
            : [];
        secondary.hidden = pairOptions.length === 0;
        if (!pairOptions.length) {
            secondaryContainer.replaceChildren();
            return;
        }

        secondaryContainer.replaceChildren(renderSecondarySlide(pairOptions[0]));
    };

    const handleFeaturedSelect = (index) => {
        const featured = events[index];
        if (!featured || !active) return;
        renderSecondary(featured.id);
        updateCountdowns(root);
    };

    featuredStack = mountEventCardStack({
        viewport: featuredViewport,
        container: featuredContainer,
        items: events,
        renderCard: renderFeaturedSlide,
        previousButton: root.querySelector('[data-events-prev]'),
        nextButton: root.querySelector('[data-events-next]'),
        dotsRoot: root.querySelector('[data-events-dots]'),
        reducedMotion,
        onChange: handleFeaturedSelect,
    });

    updateCountdowns(root);
    countdownTimer = window.setInterval(() => updateCountdowns(root), 1000);

    return () => {
        active = false;
        window.clearInterval(countdownTimer);
        featuredStack?.();
    };
};
